import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Button,
  MenuItem,
  Select,
  FormControl, 
  InputLabel,
  Pagination,
  Tabs,
  Tab,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { toast } from "react-toastify";
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import UserDetailModal from "./CustomerModel";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BASE_URL } from "../../Api/ApiUrls";
import { AuthContext } from "./../../AuthContext/AuthContext";

const Customer = () => {
  const [users, setUsers] = useState([]);
  const [rawUsers, setRawUsers] = useState([]);
  const { userId } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true); // single source of truth
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  // User type state
  const [userType, setUserType] = useState("");
  
  // Refs for debouncing and aborting
  const requestControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const latestRequestId = useRef(0);

  const User_Type = [
    "Individual Owner/Builder",
    "Landlord",
    "Agent",
    "Bank Auction",
    "Buyer",
    "Tenant",
    "Old Users",
  ];

  // Core fetch. Always resolves with { data, pagination } or null on failure.
  const fetchUsers = async (page, size, searchQuery, userTypeFilter) => {
    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      let url;
      // Use /users/type/ endpoint when user_type is selected, otherwise use /users/
      if (userTypeFilter) {
        url = `${BASE_URL}/users/type/?page=${page}&page_size=${size}&user_type=${encodeURIComponent(userTypeFilter)}`;
      } else {
        url = `${BASE_URL}/users/?page=${page}&page_size=${size}`;
      }

      if (searchQuery) {
        url += `&search_query=${encodeURIComponent(searchQuery)}`;
      }

      const res = await axios.get(url, { signal });

      const today = new Date();
      const userData = res.data?.data || res.data || [];
      const pagination = res.data?.pagination || {};

      const filteredUsers = userData.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      const transformedUsers = filteredUsers.map((user) => {
       // Get the latest login from log_history
const logs = user.log_history || [];

const latestLog = logs.reduce((latest, current) => {
  if (!current.login_time) return latest;

  if (
    !latest ||
    new Date(current.login_time) > new Date(latest.login_time)
  ) {
    return current;
  }

  return latest;
}, null);

        const subPlans = user.user_sub_plan || [];

        const activePlans = subPlans.filter(
          (plan) =>
            plan.status === true &&
            new Date(plan.expired_date) > today
        );

        const subPlanUserType = activePlans.length
          ? activePlans
              .map((plan) => {
                const count = plan.no_of_properties_unlimited
                  ? "Unlimited"
                  : plan.no_of_properties ?? 0;
                return `${plan.plan_name} - ${plan.user_type} (${count})`;
              })
              .join(", ")
          : "-";

        const activePlan = activePlans[0] || null;

        const subPlanProperties = activePlan
          ? activePlan.no_of_properties_unlimited
            ? "Unlimited"
            : activePlan.no_of_properties ?? "-"
          : "-";

        const userTypes = user.distinct_user_types || [];
        const propTypes = user.distinct_property_types || [];

        const userTypeDisplay = userTypes.length > 0 
          ? userTypes.join(", ") 
          : "-";

        const propertyTypeSummary = propTypes.length > 0
          ? propTypes.join(", ")
          : "-";

        return {
          id: user.user_id,
          name: user.first_name + " " + user.last_name || "-",
          mobile: user.mobile_no || "-",
          email: user.email || "-",
          city: user.city || "-",
          state: user.state || "-",
          address: `${user.city || "-"}, ${user.state || "-"}`,
          status: activePlans.length ? "Active" : "Inactive",
         latest_login: latestLog?.login_time || user.latest_login || null,
          user_type: userTypeDisplay,
          sub_plan_user_type: subPlanUserType,
          sub_plan_no_of_properties: subPlanProperties,
          property_type_summary: propertyTypeSummary,
          user_cart_items: user.user_cart_items?.length || 0,
          originalUser: user,
        };
      });

      return { data: transformedUsers, pagination };
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching users:", error);
        toast.error("Failed to fetch users");
      }
      return null;
    }
  };

  // Apply a successful fetch result to state, in one consistent place.
  const applyResult = (result, page) => {
    if (!result) return;
    const { data, pagination } = result;
    setUsers(data);
    setCurrentPage(pagination.current_page ?? page);
    setTotalPages(pagination.total_pages ?? 1);
    setTotalCount(pagination.total_count ?? data.length);
  };

  // Unified loader for page <= 100. Always: loading on, fetch, apply, loading off.
  const loadPage = async (page, size, searchQuery, userTypeFilter) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    const result = await fetchUsers(page, size, searchQuery, userTypeFilter);
    if (requestId !== latestRequestId.current) return; // a newer request superseded this one
    applyResult(result, page);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  // Progressive loader for page > 100, fetched in chunks of 100.
  const loadProgressively = async (page, size, searchQuery, userTypeFilter) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    setProgressiveLoading(true);

    const totalChunks = Math.ceil(size / 100);
    let allProps = [];
    let lastPagination = {};

    for (let i = 0; i < totalChunks; i++) {
      const result = await fetchUsers(page, 100, searchQuery, userTypeFilter);
      if (requestId !== latestRequestId.current) return; // superseded mid-flight
      if (result) {
        allProps = [...allProps, ...result.data];
        lastPagination = result.pagination;
        setUsers([...allProps]);
        if (i === 0) {
          // First chunk landed — table is no longer empty, drop the full-screen spinner.
          setIsLoading(false);
        }
      }
      if (i < totalChunks - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    }

    if (requestId !== latestRequestId.current) return;
    const recalculatedTotalPages = Math.ceil((lastPagination.total_count || 0) / size);
    setTotalPages(recalculatedTotalPages || 1);
    setTotalCount(lastPagination.total_count || allProps.length);
    setCurrentPage(page);
    setIsLoading(false);
    setProgressiveLoading(false);
    setHasLoadedOnce(true);
  };

  const runLoad = (page, size, searchQuery, userTypeFilter) => {
    setCurrentPage(page);
    if (size > 100) {
      loadProgressively(page, size, searchQuery, userTypeFilter);
    } else {
      loadPage(page, size, searchQuery, userTypeFilter);
    }
  };

  // Tab switch OR page size change -> reset to page 1, reload (single effect, no duplicate fetch on mount)
  useEffect(() => {
    runLoad(1, pageSize, search, userType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, pageSize]);

  // Search -> debounce, reload current page
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      runLoad(1, pageSize, search, userType);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleView = (user) => {
    setSelectedUser(user.originalUser);
    setOpenModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`${BASE_URL}/users/${userId}/`);
        toast.success("User deleted successfully");
        const nextPage = users.length <= 1 && currentPage > 1 ? currentPage - 1 : currentPage;
        runLoad(nextPage, pageSize, search, userType);
      } catch (error) {
        console.error("Failed to delete user:", error);
        toast.error("Failed to delete user");
      }
    }
  };

  const handlePageChange = (event, value) => {
    runLoad(value, pageSize, search, userType);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleUserTypeChange = (event, newValue) => {
    setUserType(newValue);
  };

  const displayedUsers = users;

  const handleExport = () => {
    if (displayedUsers.length === 0) {
      toast.warning("No data to export!");
      return;
    }

    const dataToExport = displayedUsers.map((user, index) => ({
      "S.No": index + 1,
      Name: user.name,
      Mobile: user.mobile,
      Email: user.email,
      City: user.city,
      State: user.state,
      "User Type": user.user_type,
      "Property Types": user.property_type_summary,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(data, "Customers.xlsx");
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4 }}>
      <AdminDashboard />

      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 2,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Customers Table
        </Typography>

        {/* SEARCH + DROPDOWN */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Search by Name / Mobile / Email / City / State"
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
              size="small"
              sx={{ width: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records</InputLabel>
              <Select
                value={pageSize}
                label="Records"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {progressiveLoading && (
              <CircularProgress size={20} />
            )}
          </Box>

          <Button variant="contained" color="success" onClick={handleExport}>
            Export
          </Button>
        </Box>

        {/* USER TYPE TABS */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={userType}
            onChange={handleUserTypeChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {/* ALL TAB */}
            <Tab label="All" value="" />

            {User_Type.map((type) => (
              <Tab key={type} label={type} value={type} />
            ))}
          </Tabs>
        </Box>

        {isLoading && users.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "S.No",
                    "User Details",
                   
                    "City/State",
  
                    "User Type",
                    "Property Types",
                    "Wishlist Items",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        textAlign: "center",
                        fontWeight: "bold",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedUsers.map((user, index) => (
                  <TableRow key={user.id}>
                    <TableCell align="center">{(currentPage - 1) * pageSize + index + 1}</TableCell>
                    <TableCell align="center">
                      <Box>
                        {/* Clickable Name */}
                        <span
                          onClick={() => handleView(user)}
                          style={{
                            color: "#1976d2",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 500,
                          }}
                        >
                          {user.name}
                        </span>
                                              
                                                <Typography variant="caption" display="block">
                                                  {user.mobile}
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                  {user.email}
                                                </Typography>
                                            
                               

<Typography
  variant="caption"
  display="block"
  sx={{
    mt: 0.5,
    color: "#333",
    fontWeight: 600,
  }}
>
  Last Login:{" "}
  {user.latest_login
    ? new Date(user.latest_login).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "Not Available"}
</Typography>
                      </Box>
                    </TableCell>
                    
                      <TableCell sx={{ textAlign: "center" }}>
                                          <Box>
                                            <Typography variant="body2" display="block">
                                              {user.city}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                              {user.state}
                                            </Typography>
                                           
                                          </Box>
                                        </TableCell>
                    <TableCell align="center">
                      {user.user_type}
                    </TableCell>
                    <TableCell align="center">
                      {user.property_type_summary}
                    </TableCell>
                    <TableCell align="center">
                      {user.user_cart_items}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => handleView(user)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(user.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* PAGINATION CONTROLS */}        
        {hasLoadedOnce && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {displayedUsers.length} of {totalCount} records
            </Typography>
            
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={isLoading}
              />
            )}
          </Box>
        )}

        <UserDetailModal
          open={openModal} 
          onClose={() => setOpenModal(false)}
          userid={selectedUser?.user_id}
        />
      </Box>
    </Container>
  );
};

export default Customer;